import { isEqual } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../components/button';
import { GoalStatusWithRate } from '../types';
import {
  GoalStatusInputWithRate,
  Props as GoalStatusInputWithRateProps,
} from './GoalStatusInputWithRate';

export type Props = GoalStatusInputWithRateProps;

const normalizeStatusWithRate = (
  val: GoalStatusWithRate,
): GoalStatusWithRate => ({
  status: val.status,
  rate: val.status === 'DONE' ? val.rate : 'OPEN',
  cancelReason: val.status === 'CANCELED' ? val.cancelReason : undefined,
});

export const GoalStatusInputWithRateAndSave: React.FC<
  React.PropsWithChildren<Props>
> = ({ value, onChange, ...restProps }) => {
  const { t } = useTranslation();
  const [curValue, setCurValue] = useState(() =>
    normalizeStatusWithRate(value),
  );

  const handleChange = useCallback(
    (newValue: GoalStatusWithRate) => {
      const newStatus = newValue.status;
      const isImmediateUpdate =
        newStatus === 'ON_TRACK' ||
        newStatus === 'BEHIND' ||
        newStatus === 'BEHIND_OR_ON_TRACK' ||
        (newStatus === 'DONE' && !restProps.isRateVisible);

      if (value.status !== newStatus && isImmediateUpdate) {
        const rate =
          newStatus === 'ON_TRACK' ||
          newStatus === 'BEHIND' ||
          newStatus === 'BEHIND_OR_ON_TRACK'
            ? 'OPEN'
            : value.rate;
        const immediateValue = {
          status: newStatus,
          cancelReason: undefined,
          rate,
        };
        const normalized = normalizeStatusWithRate(immediateValue);
        setCurValue(normalized);
        onChange(normalized);
        return;
      }
      setCurValue(normalizeStatusWithRate(newValue));
    },
    [value, onChange, setCurValue, restProps.isRateVisible],
  );

  const handleCancel = useCallback(() => {
    setCurValue(normalizeStatusWithRate(value));
  }, [setCurValue, value]);

  const handleSave = useCallback(() => {
    onChange(curValue);
  }, [onChange, curValue]);

  const isDirty = !isEqual(normalizeStatusWithRate(value), curValue);

  return (
    <div>
      <GoalStatusInputWithRate
        {...restProps}
        value={curValue}
        onChange={handleChange}
        availableStatuses={isDirty ? [] : restProps.availableStatuses}
      />

      {(isDirty || restProps.error) && (
        <div className="d-flex justify-content-end mt-2">
          <Button
            label={t('form.general.cancelButton')}
            size="sm"
            className="me-2"
            variant="outline-primary"
            onClick={handleCancel}
          />
          <Button
            size="sm"
            label={t('form.general.saveButton')}
            onClick={handleSave}
          />
        </div>
      )}
    </div>
  );
};
